/**
 * Sidebar Component
 * Navigation sidebar with role-based menu items
 */

import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTenant } from '../hooks/useTenant';

interface SidebarProps {
  isOpen: boolean;
  userRole: string;
}

interface MenuItem {
  path: string;
  label: string;
  icon: string;
  roles: string[];
  badge?: number;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, userRole }) => {
  const location = useLocation();
  const { currentTenant } = useAuth();
  const { branding, hasFeature } = useTenant();

  // Menu items by section
  const clientMenu: MenuItem[] = [
    { path: '/client', label: 'Dashboard', icon: '📊', roles: ['client'] },
    { path: '/client/upload', label: 'New Project', icon: '📤', roles: ['client'] },
    { path: '/client/projects', label: 'My Projects', icon: '📁', roles: ['client'] },
    { path: '/client/invoices', label: 'Invoices', icon: '💰', roles: ['client'], badge: 2 },
    { path: '/client/messages', label: 'Messages', icon: '💬', roles: ['client'] },
  ];

  const adminMenu: MenuItem[] = [
    { path: '/admin', label: 'Dashboard', icon: '📊', roles: ['owner', 'admin'] },
    { path: '/admin/projects', label: 'Projects', icon: '📁', roles: ['owner', 'admin', 'manager'] },
    { path: '/admin/team', label: 'Team', icon: '👥', roles: ['owner', 'admin'] },
    { path: '/admin/financials', label: 'Financials', icon: '💰', roles: ['owner', 'admin'] },
    { path: '/admin/standards', label: 'Standards', icon: '📐', roles: ['owner', 'admin', 'manager'] },
    { path: '/admin/rewards', label: 'Rewards', icon: '🎁', roles: ['owner', 'admin'] },
  ];

  const drafterMenu: MenuItem[] = [
    { path: '/drafter', label: 'Dashboard', icon: '📊', roles: ['drafter'] },
    { path: '/drafter/assignments', label: 'My Work', icon: '📋', roles: ['drafter'] },
    { path: '/drafter/time', label: 'Time Log', icon: '⏱️', roles: ['drafter'] },
    { path: '/drafter/standards', label: 'Standards', icon: '📐', roles: ['drafter'] },
    { path: '/drafter/rewards', label: 'Rewards', icon: '🎁', roles: ['drafter'] },
  ];

  const settingsMenu: MenuItem[] = [
    { path: '/settings/branding', label: 'Branding', icon: '🎨', roles: ['owner', 'admin'] },
    { path: '/settings/billing', label: 'Billing', icon: '💳', roles: ['owner'] },
    { path: '/settings/users', label: 'Users', icon: '👤', roles: ['owner', 'admin'] },
    { path: '/settings/integrations', label: 'Integrations', icon: '🔌', roles: ['owner', 'admin'] },
  ];

  // Get appropriate menu based on role
  const getMenuForRole = (): MenuItem[] => {
    switch (userRole) {
      case 'client':
        return clientMenu;
      case 'drafter':
        return drafterMenu;
      case 'owner':
      case 'admin':
      case 'manager':
        return adminMenu;
      default:
        return [];
    }
  };

  const mainMenu = getMenuForRole();
  const showSettings = ['owner', 'admin'].includes(userRole);
  const showAI = hasFeature('ai_drafting') && ['owner', 'admin', 'manager'].includes(userRole);

  return (
    <aside className={`sidebar ${isOpen ? 'open' : 'collapsed'}`}>
      {/* Main Navigation */}
      <nav className="sidebar-nav">
        <div className="nav-section">
          <div className="nav-section-title">{isOpen ? 'Main Menu' : ''}</div>
          {mainMenu.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => 
                `nav-item ${isActive || location.pathname === item.path ? 'active' : ''}`
              }
              title={item.label}
            >
              <span className="nav-icon">{item.icon}</span>
              {isOpen && <span className="nav-label">{item.label}</span>}
              {isOpen && item.badge && (
                <span className="nav-badge">{item.badge}</span>
              )}
            </NavLink>
          ))}
        </div>

        {/* AI Drafting (if enabled) */}
        {showAI && (
          <div className="nav-section">
            <div className="nav-section-title">{isOpen ? 'AI Tools' : ''}</div>
            <NavLink to="/admin/ai" className="nav-item" title="AI Drafting">
              <span className="nav-icon">🤖</span>
              {isOpen && <span className="nav-label">AI Drafting</span>}
              {isOpen && <span className="nav-badge new">NEW</span>}
            </NavLink>
          </div>
        )}

        {/* Settings (admin only) */}
        {showSettings && (
          <div className="nav-section">
            <div className="nav-section-title">{isOpen ? 'Settings' : ''}</div>
            {settingsMenu.map((item) => (
              item.roles.includes(userRole) && (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className="nav-item"
                  title={item.label}
                >
                  <span className="nav-icon">{item.icon}</span>
                  {isOpen && <span className="nav-label">{item.label}</span>}
                </NavLink>
              )
            ))}
          </div>
        )}
      </nav>

      {/* Sidebar Footer */}
      {isOpen && (
        <div className="sidebar-footer">
          {/* Tenant Info */}
          <div className="tenant-info">
            <span className="tier-badge">{currentTenant?.tier || 'starter'}</span>
          </div>
          
          {/* Powered By */}
          {branding.show_powered_by && (
            <div className="powered-by">
              Powered by <a href="https://shopdrawings.ai">ShopDrawings.AI</a>
            </div>
          )}
        </div>
      )}

      <style>{`
        .sidebar {
          position: fixed;
          left: 0;
          top: 64px;
          bottom: 0;
          width: 260px;
          background: var(--bg-secondary);
          border-right: 1px solid var(--border-color, #334155);
          display: flex;
          flex-direction: column;
          transition: width 0.3s ease;
          z-index: 900;
          overflow: hidden;
        }

        .sidebar.collapsed {
          width: 60px;
        }

        .sidebar-nav {
          flex: 1;
          padding: 16px 8px;
          overflow-y: auto;
        }

        .nav-section {
          margin-bottom: 24px;
        }

        .nav-section-title {
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          color: var(--text-muted, #64748b);
          padding: 0 12px;
          margin-bottom: 8px;
          height: 16px;
        }

        .nav-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 12px;
          border-radius: 8px;
          color: var(--text-secondary);
          text-decoration: none;
          transition: all 0.2s;
          margin-bottom: 2px;
        }

        .nav-item:hover {
          background: var(--bg-tertiary, #334155);
          color: var(--text-primary);
        }

        .nav-item.active {
          background: var(--primary);
          color: white;
        }

        .nav-icon {
          font-size: 18px;
          width: 24px;
          text-align: center;
          flex-shrink: 0;
        }

        .nav-label {
          font-size: 14px;
          white-space: nowrap;
        }

        .nav-badge {
          margin-left: auto;
          background: var(--danger, #ef4444);
          color: white;
          font-size: 11px;
          font-weight: 600;
          padding: 2px 6px;
          border-radius: 10px;
        }

        .nav-badge.new {
          background: var(--success, #10b981);
        }

        .sidebar.collapsed .nav-item {
          justify-content: center;
          padding: 12px;
        }

        .sidebar.collapsed .nav-label,
        .sidebar.collapsed .nav-badge,
        .sidebar.collapsed .nav-section-title {
          display: none;
        }

        .sidebar-footer {
          padding: 16px;
          border-top: 1px solid var(--border-color, #334155);
        }

        .tenant-info {
          display: flex;
          justify-content: center;
          margin-bottom: 12px;
        }

        .tier-badge {
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          padding: 4px 12px;
          border-radius: 12px;
          background: rgba(37, 99, 235, 0.1);
          color: var(--primary);
        }

        .powered-by {
          text-align: center;
          font-size: 11px;
          color: var(--text-muted, #64748b);
        }

        .powered-by a {
          color: inherit;
          text-decoration: none;
        }

        .powered-by a:hover {
          text-decoration: underline;
        }

        @media (max-width: 768px) {
          .sidebar {
            transform: translateX(-100%);
          }

          .sidebar.open {
            transform: translateX(0);
          }
        }
      `}</style>
    </aside>
  );
};

export default Sidebar;
